import { Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { DigitalUpsellSettings, TimerStatePreview } from '../widget-preview.types';
import { SmartCartPreviewStateService } from '../../smart-cart-preview-state.service';
import {
  getActiveTimerPreviewState,
  getSampleTimerCountdown,
  getTimerPreviewStyle,
} from '../../../components/timer-config/timer.model';

const DEFAULT_INNER_PADDING = 12;

@Component({
  selector: 'digital-upsell-preview',
  templateUrl: './digital-upsell-preview.component.html',
  styleUrls: ['./digital-upsell-preview.component.scss'],
  // None so the SCSS reset reaches the rich-text description/tooltip injected via [innerHTML].
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DigitalUpsellPreviewComponent {
  @Input() settings!: DigitalUpsellSettings;

  // Click flips isSelected so both colors are previewable.
  get selectorColor(): string {
    const { selectedColor, defaultColor } = this.settings.selectingMethod;
    return this.isSelected ? selectedColor : defaultColor;
  }

  get cardStyle(): Record<string, string> {
    const { card } = this.settings.appearance;
    return {
      'background-color': card.backgroundColor,
      border: `${card.borderWidth}px solid ${card.borderColor}`,
      'border-radius': `${card.cornerRadius}px`,
      padding: `${card.innerPadding ?? DEFAULT_INNER_PADDING}px`,
    };
  }

  get imageStyle(): Record<string, string> {
    const { fit, cornerRadius } = this.settings.appearance.image;
    return {
      'object-fit': fit,
      'border-radius': `${cornerRadius}px`,
    };
  }

  get formattedPrice(): string {
    if (this.settings.pricingMethod === 'percent') {
      const { minPrice, maxPrice } = this.settings.percent;
      const midpoint = (minPrice + maxPrice) / 2;
      return midpoint > 0 ? `$${midpoint.toFixed(2)}` : 'Free';
    }
    return this.settings.flatPrice ? `$${this.settings.flatPrice.toFixed(2)}` : 'Free';
  }

  isSelected = true;

  get timerEnabled(): boolean {
    return !!this.settings.timer?.enabled;
  }

  get timerPlacement(): string {
    return this.settings.timer?.placement ?? 'inline';
  }

  get showTimerStrip(): boolean {
    return this.timerEnabled && this.timerPlacement === 'inline';
  }

  get showTimerBadge(): boolean {
    return this.timerEnabled && this.timerPlacement === 'badge';
  }

  get timerMessageHtml(): string {
    const message = this.activeTimerState?.message ?? '';
    return message.replace(/\{countdown\}/g, this.sampleCountdown);
  }

  get timerAlign(): string {
    const html = this.timerMessageHtml;
    if (html.includes('ql-align-center')) return 'center';
    if (html.includes('ql-align-right')) return 'flex-end';
    return 'flex-start';
  }

  get timerIconUrl(): string {
    const icon = this.activeTimerState?.iconUrl;
    return typeof icon === 'string' ? icon : '';
  }

  get timerStyle(): Record<string, string> {
    return getTimerPreviewStyle(this.activeTimerState);
  }

  private readonly previewState = inject(SmartCartPreviewStateService);

  private get activeTimerState(): TimerStatePreview | undefined {
    return getActiveTimerPreviewState(this.settings.timer, this.previewState.digitalUpsellPreviewTimerState());
  }

  private get sampleCountdown(): string {
    return getSampleTimerCountdown(this.previewState.digitalUpsellPreviewTimerState(), this.settings.timer?.format);
  }

  toggleSelected(): void {
    this.isSelected = !this.isSelected;
  }
}
